import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Lock, CheckCircle, ArrowRight, Mail } from "lucide-react";
import { API } from "../config";

const PasswordResetPage = () => {
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/password-reset/request`, { email });
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast.success("Si el email existe, recibirás instrucciones");
      setStep("confirm");
    } catch (error) { toast.error("Error al procesar solicitud"); }
    finally { setLoading(false); }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error("Las contraseñas no coinciden"); return; }
    if (newPassword.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { token: resetToken, new_password: newPassword });
      toast.success("Contraseña restablecida exitosamente");
      setStep("done");
    } catch (error) { toast.error(error.response?.data?.detail || "Error al restablecer"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6] flex items-center justify-center" data-testid="password-reset-page">
      <div className="w-full max-w-md px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bento-card p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center mx-auto mb-4"><Lock size={32} className="text-white" /></div>
            <h1 className="text-2xl font-bold text-[#5D4037]">
              {step === "request" ? "Recuperar Contraseña" : step === "confirm" ? "Nueva Contraseña" : "¡Listo!"}
            </h1>
          </div>

          {step === "request" && (
            <form onSubmit={handleRequest} className="space-y-4">
              <p className="text-stone-600 text-sm text-center mb-4">Ingresa tu email y te enviaremos instrucciones para restablecer tu contraseña.</p>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" required data-testid="reset-email" /></div>
              <button type="submit" disabled={loading} className="w-full btn-primary h-12" data-testid="reset-request-btn">{loading ? "Enviando..." : "Enviar Instrucciones"}</button>
              <Link to="/login" className="block text-center text-sm text-[#2E7D32] font-medium hover:underline mt-4">Volver a Iniciar Sesión</Link>
            </form>
          )}

          {step === "confirm" && (
            <form onSubmit={handleConfirm} className="space-y-4">
              <p className="text-stone-600 text-sm text-center mb-4">Ingresa el token de recuperación y tu nueva contraseña.</p>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Token de recuperación</label><input type="text" value={resetToken} onChange={(e) => setResetToken(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none font-mono text-sm" required data-testid="reset-token" /></div>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Nueva contraseña</label><input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" required data-testid="reset-new-pw" /></div>
              <div><label className="block text-sm font-semibold text-[#5D4037] mb-2">Confirmar contraseña</label><input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-stone-200 outline-none" required data-testid="reset-confirm-pw" /></div>
              <button type="submit" disabled={loading} className="w-full btn-primary h-12" data-testid="reset-confirm-btn">{loading ? "Restableciendo..." : "Restablecer Contraseña"}</button>
            </form>
          )}

          {step === "done" && (
            <div className="text-center">
              <CheckCircle size={48} className="text-[#4CAF50] mx-auto mb-4" />
              <p className="text-stone-600 mb-6">Tu contraseña ha sido restablecida exitosamente.</p>
              <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="go-to-login-btn">Iniciar Sesión <ArrowRight size={18} /></Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
